import { useEffect, useState } from 'react';
import {
  GetRoiTableViewReq,
  RoiSmsData,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import dayjs from 'dayjs';
import { AnalyticsCommonTypes } from '@frontend/api-analytics';
import { AnalyticsHooks } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { useSchemaInfiniteQuery } from '@frontend/react-query-helpers';
import { SchemaAnalyticsGatewayService, SchemaIO } from '@frontend/schema';
import { useAlert } from '@frontend/design-system';
import { patientsDemoData } from '../common-demo-data/patients';
import { commonApiOptions } from '../helpers/api-helpers';

interface UseFetchRoiPatientsProps {
  isDemoAccount?: boolean;
  isEnabled?: boolean;
  payload: GetRoiTableViewReq;
}

interface UseFetchRoiPatientsResponse {
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  isLoading?: boolean;
  pageConfig: AnalyticsCommonTypes.PageConfig;
  patients: RoiSmsData[];
  setPageConfig: (pageConfig: AnalyticsCommonTypes.PageConfig) => void;
}

const DEFAULT_PAGE_SIZE = 10;

export const useFetchRoiPatients = ({
  isDemoAccount,
  isEnabled = true,
  payload,
}: UseFetchRoiPatientsProps): UseFetchRoiPatientsResponse => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { isMulti, orgId } = AnalyticsHooks.useIsMulti();

  const [pageConfig, setPageConfig] = useState<AnalyticsCommonTypes.PageConfig>({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const enableFetch =
    !isDemoAccount &&
    isEnabled &&
    !!payload?.smsRequest?.startDate &&
    !!payload?.smsRequest.endDate &&
    !!payload?.smsRequest.locationId?.length;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    hasPreviousPage,
    isFetching: isLoading,
  } = useSchemaInfiniteQuery<
    typeof SchemaAnalyticsGatewayService,
    'GetRoiSMS',
    unknown,
    SchemaIO<(typeof SchemaAnalyticsGatewayService)['GetRoiSMS']>['output']
  >({
    service: SchemaAnalyticsGatewayService,
    serviceName: 'analytics_gateway patients',
    endpointName: 'GetRoiSMS',
    httpOptions: {
      params: {
        limit: pageConfig.pageSize,
        skip: pageConfig.pageSize * (pageConfig.pageNumber - 1),
      },
    },
    request: () => {
      return payload.smsRequest
        ? {
            ...payload,
            smsRequest: {
              ...payload.smsRequest,
              startDate: payload.smsRequest?.startDate
                ? `${dayjs(payload.smsRequest.startDate).format('YYYY-MM-DD')}T00:00:00.000`
                : '',
              endDate: payload.smsRequest?.endDate
                ? `${dayjs(payload.smsRequest.endDate).format('YYYY-MM-DD')}T23:59:59.999`
                : '',
              isMulti,
              orgId,
            },
          }
        : payload;
    },
    options: {
      ...commonApiOptions,
      getNextPageParam: (lastPage) => {
        return !!lastPage?.data?.data?.length;
      },
      getPreviousPageParam: () => {
        return pageConfig.pageNumber > 1;
      },
      onError: () => {
        alert.error(t("Couldn't fetch the patients details. Please try again."));
      },
      enabled: enableFetch,
    },
  });

  useEffect(() => {
    setPageConfig({ ...pageConfig, pageNumber: 1 });
  }, [JSON.stringify(payload)]);

  return {
    fetchNextPage,
    hasNextPage,
    hasPreviousPage,
    isLoading,
    pageConfig,
    patients: isDemoAccount
      ? patientsDemoData(payload?.smsRequest?.locationId ?? [])
      : // Refer to the helper note at the end of the file to know the reason for using 0 index
        data?.pages[0]?.data?.data ?? [],
    setPageConfig,
  };
};

/*
 * Helper Note:
 * Because the API expects the page limit and offset to be passed in the request,
 * Query key for every request is updated automatically when the skip value changes.
 * Hence we would always use the 0 index to read the page data
 */
